import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    RefreshCw, ShieldQuestion, ExternalLink, Check, X, PlayCircle,
    Globe, Search,
} from "lucide-react";

/**
 * Fase 6.9 + 7.8 · Controllo qualità dati.
 *
 * - Segnalazioni origin='mexal' (producer/name/vintage/type/format):
 *   informativa, correzione a monte nel gestionale.
 * - Segnalazioni origin='scheda' (country/region/provincia/denomination/
 *   grapes): auto-applicabile con conferma dialog che mostra prima→dopo.
 * - Web grounding (Google Search) opzionale via toggle in AdminConfig;
 *   quando presente, la segnalazione mostra 1-2 fonti web.
 */
const CONF_BADGE = {
    high: "bg-red-100 text-red-800",
    medium: "bg-amber-100 text-amber-800",
    low: "bg-slate-100 text-slate-700",
};

const FIELD_LABEL = {
    producer: "Produttore",
    name: "Nome vino",
    vintage: "Annata",
    type: "Tipologia",
    format: "Formato",
    country: "Paese",
    region: "Regione",
    provincia: "Provincia",
    denomination: "Denominazione",
    grapes: "Uvaggio",
};

const ORIGIN_META = {
    mexal: {
        label: "Mexal",
        cls: "bg-slate-100 text-slate-700 border-slate-300",
        hint: "Correzione a monte nel gestionale",
    },
    scheda: {
        label: "Scheda",
        cls: "bg-[#F4E9DC] text-[#5B1C2C] border-[#D9B98F]",
        hint: "Auto-applicabile sulla scheda vino",
    },
};

export default function AdminDataQuality() {
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [statusFilter, setStatusFilter] = useState("open");
    const [originFilter, setOriginFilter] = useState("all");
    const [confirmIssue, setConfirmIssue] = useState(null);   // issue in attesa di conferma apply
    const [scanningWineId, setScanningWineId] = useState(null);

    const load = () => {
        setLoading(true);
        const params = { status: statusFilter, limit: 500 };
        if (originFilter !== "all") params.origin = originFilter;
        api.get("/admin/data-quality/issues", { params })
            .then(({ data }) => setItems(data.items || []))
            .catch(() => toast.error("Errore caricamento segnalazioni"))
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [statusFilter, originFilter]);

    const scan = async () => {
        setScanning(true);
        try {
            const { data } = await api.post("/admin/data-quality/scan?limit=50", null, { timeout: 240_000 });
            const groundedNote = data.grounded_calls
                ? ` · web usato ${data.grounded_calls}/${data.web_quota} (oggi ${data.web_used_today})`
                : "";
            toast.success(
                `Scan completato · ${data.issues_created} nuove, ${data.issues_updated} aggiornate su ${data.scanned} vini${groundedNote}`,
            );
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore scan");
        } finally { setScanning(false); }
    };

    const scanOne = async (wineId) => {
        setScanningWineId(wineId);
        try {
            const { data } = await api.post(`/admin/data-quality/scan-one/${wineId}`, null, { timeout: 120_000 });
            const groundedNote = data.grounded ? " (web verificato)" : "";
            toast.success(
                `Scan singolo · ${data.issues_created} nuove, ${data.issues_updated} aggiornate${groundedNote}`,
            );
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore scan singolo");
        } finally { setScanningWineId(null); }
    };

    const requestApply = (issue) => {
        if (issue.origin !== "scheda") {
            toast.info("Segnalazione Mexal: correggi il dato a monte nel gestionale.");
            return;
        }
        setConfirmIssue(issue);
    };

    const confirmApply = async () => {
        if (!confirmIssue) return;
        const id = confirmIssue.id;
        setConfirmIssue(null);
        try {
            await api.post(`/admin/data-quality/issues/${id}/apply`);
            toast.success("Suggerimento applicato alla scheda vino");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    const ignore = async (id) => {
        try {
            await api.post(`/admin/data-quality/issues/${id}/ignore`);
            toast.success("Segnalazione ignorata");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    return (
        <div className="p-6 max-w-6xl mx-auto" data-testid="admin-data-quality-page">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-3">
                <div>
                    <h1 className="text-2xl flex items-center gap-2">
                        <ShieldQuestion className="w-5 h-5" strokeWidth={1.5} />
                        Controllo qualità dati
                    </h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
                        L'AI confronta i campi importati da Mexal e quelli
                        arricchiti in scheda con la propria conoscenza dei
                        vini e — quando abilitato — con la ricerca web
                        Google (Gemini grounding). Le segnalazioni
                        <b> Mexal</b> vanno corrette a monte nel gestionale;
                        quelle <b>Scheda</b> sono <b>auto-applicabili</b>
                        con conferma.
                    </p>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl italic">
                        Nota: se in <b>Configurazione</b> il flag «Mostra al
                        pubblico anche le schede in attesa di validazione» è
                        OFF, le correzioni applicate su uvaggio e
                        denominazione compaiono al pubblico solo dopo la
                        validazione del sommelier.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="text-sm border border-[hsl(var(--border))] rounded-sm px-2 py-1 bg-white"
                        data-testid="dq-status-filter"
                    >
                        <option value="open">Aperte</option>
                        <option value="applied">Applicate</option>
                        <option value="ignored">Ignorate</option>
                        <option value="all">Tutte</option>
                    </select>
                    <select
                        value={originFilter}
                        onChange={(e) => setOriginFilter(e.target.value)}
                        className="text-sm border border-[hsl(var(--border))] rounded-sm px-2 py-1 bg-white"
                        data-testid="dq-origin-filter"
                    >
                        <option value="all">Tutte origini</option>
                        <option value="scheda">Solo Scheda</option>
                        <option value="mexal">Solo Mexal</option>
                    </select>
                    <button className="co-btn-ghost" onClick={load} disabled={loading} data-testid="dq-refresh">
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    </button>
                    <button className="co-btn-primary" onClick={scan} disabled={scanning} data-testid="dq-scan-btn">
                        <PlayCircle className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} strokeWidth={1.5} />
                        {scanning ? "Scan in corso…" : "Scan AI (50 vini)"}
                    </button>
                </div>
            </div>

            {!items ? (
                <div className="text-sm text-[hsl(var(--muted-foreground))]">Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="p-6 bg-[#FDFBF7] border border-[hsl(var(--border))] rounded-sm text-center" data-testid="dq-empty">
                    Nessuna segnalazione {statusFilter !== "all" ? `(${statusFilter})` : ""}
                    {originFilter !== "all" ? ` · origine ${originFilter}` : ""}.
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
                        Lancia uno scan AI (in alto) o uno scan mirato dalla scheda vino.
                        Il worker gira anche automaticamente ogni 4h.
                    </div>
                </div>
            ) : (
                <>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3" data-testid="dq-total">
                        <b>{items.length}</b> segnalazion{items.length === 1 ? "e" : "i"} — ordinate: web-verificate → confidenza → data.
                    </p>
                    <div className="bg-white border border-[hsl(var(--border))] rounded-sm overflow-x-auto">
                        <table className="w-full text-sm" data-testid="dq-table">
                            <thead className="bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] text-[11px] uppercase tracking-wider">
                                <tr>
                                    <th className="text-left p-3">SKU · Vino</th>
                                    <th className="text-left p-3">Origine · Campo</th>
                                    <th className="text-left p-3">Valore attuale</th>
                                    <th className="text-left p-3">Suggerito AI</th>
                                    <th className="text-left p-3">Motivo · Fonti</th>
                                    <th className="text-left p-3">Confidenza</th>
                                    <th className="text-right p-3">Azioni</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[hsl(var(--border))]">
                                {items.map((it) => {
                                    const originMeta = ORIGIN_META[it.origin] || ORIGIN_META.mexal;
                                    const currentVal = it.origin === "scheda"
                                        ? (it.current_value || "")
                                        : (it.mexal_value || "");
                                    return (
                                        <tr key={it.id} data-testid={`dq-row-${it.id}`}>
                                            <td className="p-3">
                                                <div className="font-mono text-xs">{it.mexal_sku || "—"}</div>
                                                <div className="text-[11px]">{it.wine_producer} · {it.wine_name}</div>
                                            </td>
                                            <td className="p-3">
                                                <span
                                                    className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${originMeta.cls}`}
                                                    title={originMeta.hint}
                                                    data-testid={`dq-origin-${it.id}`}
                                                >
                                                    {originMeta.label}
                                                </span>
                                                {/* Fase 8.7.1 · badge "scheda validata": incongruenza rilevata su una
                                                    scheda già `validato_sommelier` — vale la pena guardarci con attenzione. */}
                                                {it.on_validated && (
                                                    <span
                                                        className="ml-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#5B1C2C] text-white"
                                                        title="Segnalazione su scheda già validata dal sommelier — verifica con attenzione."
                                                        data-testid={`dq-on-validated-${it.id}`}
                                                    >
                                                        Scheda validata
                                                    </span>
                                                )}
                                                <div className="font-medium mt-1">{FIELD_LABEL[it.field] || it.field}</div>
                                            </td>
                                            <td className="p-3 italic text-[hsl(var(--muted-foreground))] max-w-[160px] truncate" title={currentVal}>
                                                {currentVal || "—"}
                                            </td>
                                            <td className="p-3 font-medium">{it.suggested_value || "—"}</td>
                                            <td className="p-3 text-[11px] text-[hsl(var(--muted-foreground))] max-w-[280px]">
                                                <div>{it.reason}</div>
                                                {it.web_verified && (
                                                    <div className="mt-1 flex items-center gap-1 text-[10px] text-[#5B1C2C]" data-testid={`dq-web-verified-${it.id}`}>
                                                        <Globe className="w-3 h-3" strokeWidth={1.5} />
                                                        <span>Verificato via ricerca web</span>
                                                    </div>
                                                )}
                                                {Array.isArray(it.sources) && it.sources.length > 0 && (
                                                    <div className="mt-1 flex flex-col gap-0.5">
                                                        {it.sources.slice(0, 2).map((s, idx) => (
                                                            <a
                                                                key={idx}
                                                                href={s.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-[10px] underline text-[#5B1C2C] hover:text-[#7a2938] truncate max-w-[240px]"
                                                                title={s.url}
                                                                data-testid={`dq-source-${it.id}-${idx}`}
                                                            >
                                                                {s.title || s.url}
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-3">
                                                <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider ${CONF_BADGE[it.confidence] || ""}`}>
                                                    {it.confidence}
                                                </span>
                                            </td>
                                            <td className="p-3 text-right">
                                                {it.status === "open" ? (
                                                    <div className="flex items-center justify-end gap-1 flex-wrap">
                                                        <button
                                                            onClick={() => requestApply(it)}
                                                            disabled={it.origin !== "scheda"}
                                                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm ${it.origin === "scheda"
                                                                ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
                                                                : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
                                                            title={it.origin === "scheda"
                                                                ? "Applica il suggerimento alla scheda vino"
                                                                : "Segnalazione Mexal: correggi a monte nel gestionale"}
                                                            data-testid={`dq-apply-${it.id}`}
                                                        >
                                                            <Check className="w-3 h-3" strokeWidth={1.5} />
                                                            Applica
                                                        </button>
                                                        <button
                                                            onClick={() => scanOne(it.wine_id)}
                                                            disabled={scanningWineId === it.wine_id}
                                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[hsl(var(--border))] rounded-sm hover:bg-[hsl(var(--secondary))]"
                                                            title="Ri-scan singolo vino con ricerca web"
                                                            data-testid={`dq-scan-one-${it.id}`}
                                                        >
                                                            <Search className={`w-3 h-3 ${scanningWineId === it.wine_id ? "animate-spin" : ""}`} strokeWidth={1.5} />
                                                        </button>
                                                        <button
                                                            onClick={() => ignore(it.id)}
                                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[hsl(var(--border))] rounded-sm hover:bg-[hsl(var(--secondary))]"
                                                            data-testid={`dq-ignore-${it.id}`}
                                                        >
                                                            <X className="w-3 h-3" strokeWidth={1.5} />
                                                            Ignora
                                                        </button>
                                                        <Link
                                                            to={`/admin/vini/${it.wine_id}`}
                                                            className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] px-2"
                                                            data-testid={`dq-edit-${it.id}`}
                                                        >
                                                            Scheda <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                                                        </Link>
                                                    </div>
                                                ) : (
                                                    <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                                                        {it.status}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Dialog di conferma per apply auto-applicabile ---------- */}
            <AlertDialog open={!!confirmIssue} onOpenChange={(o) => !o && setConfirmIssue(null)}>
                <AlertDialogContent data-testid="dq-apply-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Applica suggerimento alla scheda vino?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Vino</div>
                                    <div className="font-medium">
                                        {confirmIssue?.wine_producer} · {confirmIssue?.wine_name}
                                        {confirmIssue?.wine_vintage && <span className="italic ml-1">{confirmIssue.wine_vintage}</span>}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Campo</div>
                                    <div className="font-medium">{FIELD_LABEL[confirmIssue?.field] || confirmIssue?.field}</div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-2 bg-slate-50 border border-[hsl(var(--border))] rounded-sm">
                                        <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Prima</div>
                                        <div className="italic break-words">
                                            {confirmIssue?.current_value || <span className="text-[hsl(var(--muted-foreground))]">(vuoto)</span>}
                                        </div>
                                    </div>
                                    <div className="p-2 bg-[#F4E9DC]/50 border border-[#D9B98F] rounded-sm">
                                        <div className="text-[10px] uppercase tracking-wider text-[#5B1C2C]">Dopo</div>
                                        <div className="font-medium break-words">
                                            {confirmIssue?.suggested_value || <span className="text-[hsl(var(--muted-foreground))]">(vuoto)</span>}
                                        </div>
                                    </div>
                                </div>
                                {confirmIssue?.reason && (
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] italic">
                                        {confirmIssue.reason}
                                    </div>
                                )}
                                {(confirmIssue?.field === "grapes" || confirmIssue?.field === "denomination") && (
                                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] bg-[#F5EFE7] border border-[hsl(var(--border))] rounded-sm px-2 py-1.5">
                                        Se «Mostra schede in attesa di validazione» è OFF in
                                        Configurazione, questa correzione sarà visibile al pubblico
                                        solo dopo la validazione del sommelier.
                                    </div>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="dq-apply-confirm-cancel">Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmApply} data-testid="dq-apply-confirm-ok">
                            Applica
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
